import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const CTASectionMobile = ({ section, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "content_centered"
  const showBackgroundColor = settings.showBackgroundColor || false
  const backgroundColor = settings.backgroundColor || "#ffffff"
  // Get highlights from settings with fallback to legacy highlights column
  const highlights = settings.highlights || section.highlights || []

  const renderContent = (textAlign = "center") => {
    // Get text colors from section settings with fallbacks
    const titleColor = settings.titleColor || theme?.colors?.textDark || "#0b3954"
    const subheadlineColor = settings.subheadlineColor || theme?.colors?.textLight || "#55778a"
    const descriptionColor = settings.descriptionColor || theme?.colors?.textDark || "#0b3954"

    return (
      <div className={`text-${textAlign} space-y-3 md:space-y-4`}>
        {section.headline && (
          <h2
            className="text-[24px] font-bold leading-[28px] lg:text-4xl md:text-3xl"
            style={{
              fontFamily: theme?.fonts?.heading || "serif",
              fontWeight: theme?.fonts?.headingWeight || "700",
              color: titleColor
            }}>
            {section.headline}
          </h2>
        )}
        {section.subheadline && (
          <p
            className="text-[16px] leading-[22px] lg:text-xl md:text-lg"
            style={{
              fontFamily: theme?.fonts?.paragraph || "sans-serif",
              color: subheadlineColor
            }}>
            {section.subheadline}
          </p>
        )}
        {section.body && (
          <>
            <style>{`
              .cta-section-content ul {
                list-style-type: disc !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .cta-section-content li {
                margin-bottom: 0.5rem !important;
                display: list-item !important;
              }
              .cta-section-content ol {
                list-style-type: decimal !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .cta-section-content p {
                margin-bottom: 1rem !important;
              }
              .cta-section-content h1, .cta-section-content h2, .cta-section-content h3,
              .cta-section-content h4, .cta-section-content h5, .cta-section-content h6 {
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
              }
            `}</style>
            <div
              className="cta-section-content prose prose-lg w-full max-w-none text-[14px] leading-[20px] md:text-base"
              style={{
                fontFamily: theme?.fonts?.paragraph || "sans-serif",
                color: descriptionColor
              }}
              dangerouslySetInnerHTML={{ __html: section.body }}
            />
          </>
        )}
      </div>
    )
  }

  const renderHighlight = (highlight, index) => {
    // Get text colors from section settings with fallbacks
    const titleColor = settings.titleColor || theme?.colors?.textDark || "#0b3954"
    const descriptionColor = settings.descriptionColor || theme?.colors?.textDark || "#0b3954"
    const highlightTitleFontSize = settings.highlightTitleFontSize || "18px"
    const highlightDescriptionFontSize = settings.highlightDescriptionFontSize || "14px"

    return (
      <div key={index} className="flex flex-col items-center space-y-2 text-center md:space-y-3">
        {highlight.imageUrl && (
          <div className="mb-2 md:mb-3">
            <img
              src={highlight.imageUrl}
              alt={highlight.title || `Highlight ${index + 1}`}
              className="h-20 w-20 rounded-lg object-cover shadow-lg md:h-24 md:w-24"
            />
          </div>
        )}
        {highlight.title && (
          <h3
            className="font-semibold leading-[22px] md:text-xl"
            style={{
              fontFamily: theme?.fonts?.heading || "serif",
              color: titleColor,
              fontSize: highlightTitleFontSize
            }}>
            {highlight.title}
          </h3>
        )}
        {highlight.description && (
          <div
            className="leading-[18px] md:text-base"
            style={{
              fontFamily: theme?.fonts?.paragraph || "sans-serif",
              color: descriptionColor,
              fontSize: highlightDescriptionFontSize
            }}
            dangerouslySetInnerHTML={{
              __html: typeof highlight.description === "object" ? highlight.description.html : highlight.description
            }}
          />
        )}
      </div>
    )
  }

  // Layout-specific rendering
  switch (layout) {
    case "content_centered":
      return (
        <section
          className={twMerge("relative py-8 md:py-12", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor: showBackgroundColor ? backgroundColor : "transparent" }}>
          <div className="mx-auto w-full px-4 md:px-4">
            <AnimatedSection>
              {section.imageUrl && (
                <div className="mb-6 flex justify-center">
                  <div className="relative">
                    <div
                      className="absolute -bottom-4 -right-4 h-16 w-12 rounded-lg"
                      style={{ backgroundColor: theme?.colors?.accent || "#E6DAD7" }}
                    />
                    <img
                      src={section.imageUrl}
                      alt={section.headline || "CTA image"}
                      className="relative h-48 w-48 rounded-lg object-cover"
                    />
                  </div>
                </div>
              )}
              {renderContent("center")}
            </AnimatedSection>
          </div>
        </section>
      )

    case "image_right":
      return (
        <section
          className={twMerge("relative py-8 md:py-12", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor: showBackgroundColor ? backgroundColor : "transparent" }}>
          <div className="mx-auto max-w-6xl px-4 md:px-4">
            <div className="flex flex-col items-center gap-6 md:gap-8">
              <AnimatedSection>{renderContent("center")}</AnimatedSection>
              {section.imageUrl && (
                <div className="relative w-full">
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "CTA image"}
                    className="h-auto w-full rounded-lg shadow-xl"
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )

    case "image_left":
      return (
        <section
          className={twMerge("relative py-8 md:py-12", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor: showBackgroundColor ? backgroundColor : "transparent" }}>
          <div className="mx-auto max-w-6xl px-4 md:px-4">
            <div className="flex flex-col items-center gap-6 md:gap-8">
              {section.imageUrl && (
                <div className="relative w-full">
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "CTA image"}
                    className="h-auto w-full rounded-lg shadow-xl"
                  />
                </div>
              )}
              <AnimatedSection>{renderContent("center")}</AnimatedSection>
            </div>
          </div>
        </section>
      )

    case "half_image_right":
      return (
        <section
          className={twMerge("relative py-8 md:py-12", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor: showBackgroundColor ? backgroundColor : "transparent" }}>
          <div className="mx-auto max-w-6xl px-4 md:px-4">
            <div className="relative flex flex-col items-center gap-4 md:gap-6">
              <AnimatedSection>{renderContent("center")}</AnimatedSection>
              {section.imageUrl && (
                <div className="relative w-full">
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "CTA image"}
                    className="h-auto w-full rounded-lg shadow-xl"
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )

    case "half_image_left":
      return (
        <section
          className={twMerge("relative py-8 md:py-12", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor: showBackgroundColor ? backgroundColor : "transparent" }}>
          <div className="mx-auto max-w-6xl px-4 md:px-4">
            <div className="relative flex flex-col items-center gap-4 md:gap-6">
              {section.imageUrl && (
                <div className="relative w-full">
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "CTA image"}
                    className="h-auto w-full rounded-lg shadow-xl"
                  />
                </div>
              )}
              <AnimatedSection>{renderContent("center")}</AnimatedSection>
            </div>
          </div>
        </section>
      )

    case "highlights_2":
      return (
        <section
          className={twMerge("relative py-8 md:py-12", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor: showBackgroundColor ? backgroundColor : "transparent" }}>
          <div className="mx-auto max-w-6xl px-4 md:px-4">
            <AnimatedSection className="mb-6 md:mb-8">{renderContent("center")}</AnimatedSection>
            <div className="grid w-full grid-cols-2 gap-4 lg:gap-6">
              {highlights.slice(0, 2).map((highlight, index) => renderHighlight(highlight, index))}
            </div>
          </div>
        </section>
      )

    case "highlights_3":
      return (
        <section
          className={twMerge("relative py-8 md:py-12", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor: showBackgroundColor ? backgroundColor : "transparent" }}>
          <div className="mx-auto max-w-6xl px-4 md:px-4">
            <AnimatedSection className="mb-6 md:mb-8">{renderContent("center")}</AnimatedSection>
            <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
              {highlights.slice(0, 3).map((highlight, index) => renderHighlight(highlight, index))}
            </div>
          </div>
        </section>
      )

    default:
      return (
        <section
          className={twMerge("relative py-8 md:py-12", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor: showBackgroundColor ? backgroundColor : "transparent" }}>
          <div className="mx-auto w-full px-4 md:px-4">
            <AnimatedSection>{renderContent("center")}</AnimatedSection>
          </div>
        </section>
      )
  }
}

export default CTASectionMobile
